import React from 'react';
import { Filter } from './types';

type RenderFunc<F> = (props: { filter: F; setFilter: (filter: F) => void }) => React.ReactNode;

export interface UseFilterReturnType<R, F> extends Filter<R> {
  filterState: F;
}

/**
 * Function to create a filter that filters rows, renders its UI and keeps track of its own state.
 * Also see the prebuilt filters useSearchFilter and useMenuFilter.
 * @param R The type of the rows to filter.
 * @param F The type of the filter value (for example, string if your filter is text-based).
 * @param key React key to use for rendering this filter.
 * @param initialFilter Initial state for the filter.
 * @param predicate Function that returns true if the row should be rendered given the filter, false if not.
 * @param render Function to render the filter component given the current filter state.
 */
export function useFilter<R, F>(
  key: string,
  initialFilter: F,
  predicate: (row: R, filter: F) => boolean,
  render: RenderFunc<F>,
): UseFilterReturnType<R, F> {
  return useAsyncFilter(
    key,
    initialFilter,
    React.useCallback(
      async (rows, filter) => rows.filter(row => predicate(row, filter)),
      [predicate],
    ),
    render,
  );
}

/**
 * Async version of useFilter.
 * Also see the prebuilt filters useAsyncSearchFilter and useAsyncMenuFilter.
 * @param R The type of the rows to filter.
 * @param F The type of the filter value (for example, string if your filter is text-based).
 * @param key React key to use for rendering this filter.
 * @param initialFilter Initial state for the filter.
 * @param filterRows Async function to filter the entire set of rows.
 * @param render Function to render the filter component given the current filter state.
 */
export function useAsyncFilter<R, F>(
  key: string,
  initialFilter: F,
  filterRows: (rows: R[], filter: F) => Promise<R[]>,
  render: RenderFunc<F>,
): UseFilterReturnType<R, F> {
  const [filter, setFilter] = React.useState(initialFilter);
  return React.useMemo(
    () => ({
      key,
      filterRows: rows => filterRows(rows, filter),
      render: () => render({ filter, setFilter }),
      filterState: filter,
    }),
    [filter, filterRows, key, render],
  );
}
